import { useEffect, useMemo, useState } from 'react'
import { Activity, CalendarCheck2, CalendarDays, Layers3, Newspaper, Repeat2, Rows3, Search, TriangleAlert, User, Users, Users2, Zap } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type AdminWidgetId = 'pipeline' | 'myArticles' | 'needsAttention' | 'frontPages' | 'recentActivity' | 'teamLoad' | 'draftDistribution' | 'frontPageReadiness' | 'recentActivityTrend' | 'readerImpact' | 'publishingCadence' | 'contentMix' | 'qualityChurn' | 'searchSignals'
export type AdminWidgetWidth = 1 | 2

export interface AdminWidgetItem {
  id: AdminWidgetId
  width: AdminWidgetWidth
}

export interface AdminWidgetDefinition {
  id: AdminWidgetId
  title: string
  description: string
  defaultWidth: AdminWidgetWidth
  icon: LucideIcon
}

export const ADMIN_WIDGET_STORAGE_KEY = 'wire-ui.admin-dashboard.widgets.v1'

export const ADMIN_WIDGET_DEFINITIONS: Record<AdminWidgetId, AdminWidgetDefinition> = {
  pipeline: {
    id: 'pipeline',
    title: 'Pipeline Gauges',
    description: 'Overall load and blockers across the workflow.',
    defaultWidth: 2,
    icon: Zap,
  },
  myArticles: {
    id: 'myArticles',
    title: 'My Articles',
    description: 'Work assigned directly to you.',
    defaultWidth: 1,
    icon: User,
  },
  needsAttention: {
    id: 'needsAttention',
    title: 'Needs Attention',
    description: 'Blocked and unassigned work.',
    defaultWidth: 1,
    icon: TriangleAlert,
  },
  frontPages: {
    id: 'frontPages',
    title: 'Front Pages',
    description: 'What is live and what is next.',
    defaultWidth: 1,
    icon: Newspaper,
  },
  recentActivity: {
    id: 'recentActivity',
    title: 'Recent Activity',
    description: 'Latest workflow transitions.',
    defaultWidth: 1,
    icon: Activity,
  },
  teamLoad: {
    id: 'teamLoad',
    title: 'Team Load',
    description: 'Role balance and assignment coverage.',
    defaultWidth: 1,
    icon: Users,
  },
  draftDistribution: {
    id: 'draftDistribution',
    title: 'Draft Distribution',
    description: 'How drafts are distributed by workflow stage.',
    defaultWidth: 1,
    icon: Rows3,
  },
  frontPageReadiness: {
    id: 'frontPageReadiness',
    title: 'Front Page Readiness',
    description: 'Live and scheduled front page status.',
    defaultWidth: 1,
    icon: CalendarCheck2,
  },
  recentActivityTrend: {
    id: 'recentActivityTrend',
    title: 'Recent Activity Trend',
    description: 'Workflow transitions by day.',
    defaultWidth: 1,
    icon: Activity,
  },
  readerImpact: {
    id: 'readerImpact',
    title: 'Reader Impact',
    description: 'Top articles by readership metrics.',
    defaultWidth: 1,
    icon: Users2,
  },
  publishingCadence: {
    id: 'publishingCadence',
    title: 'Publishing Cadence',
    description: 'Upcoming scheduled draft volume by day.',
    defaultWidth: 1,
    icon: CalendarDays,
  },
  contentMix: {
    id: 'contentMix',
    title: 'Content Mix',
    description: 'Distribution of draft product types.',
    defaultWidth: 1,
    icon: Layers3,
  },
  qualityChurn: {
    id: 'qualityChurn',
    title: 'Quality Churn',
    description: 'Re-entry and bounceback pressure in draft workflow.',
    defaultWidth: 1,
    icon: Repeat2,
  },
  searchSignals: {
    id: 'searchSignals',
    title: 'Search Signals',
    description: 'Search demand and discoverability pressure.',
    defaultWidth: 1,
    icon: Search,
  },
}

export const DEFAULT_ADMIN_WIDGET_LAYOUT: AdminWidgetItem[] = [
  { id: 'pipeline', width: 2 },
  { id: 'myArticles', width: 1 },
  { id: 'needsAttention', width: 1 },
  { id: 'frontPages', width: 1 },
  { id: 'recentActivity', width: 1 },
]

function isWidgetId(value: unknown): value is AdminWidgetId {
  return value === 'pipeline'
    || value === 'myArticles'
    || value === 'needsAttention'
    || value === 'frontPages'
    || value === 'recentActivity'
    || value === 'teamLoad'
    || value === 'draftDistribution'
    || value === 'frontPageReadiness'
    || value === 'recentActivityTrend'
    || value === 'readerImpact'
    || value === 'publishingCadence'
    || value === 'contentMix'
    || value === 'qualityChurn'
    || value === 'searchSignals'
}

function isWidgetWidth(value: unknown): value is AdminWidgetWidth {
  return value === 1 || value === 2
}

function makeDefaultItem(id: AdminWidgetId): AdminWidgetItem {
  return { id, width: ADMIN_WIDGET_DEFINITIONS[id].defaultWidth }
}

function sanitizeLayout(raw: unknown): AdminWidgetItem[] {
  if (!raw || typeof raw !== 'object') {
    return DEFAULT_ADMIN_WIDGET_LAYOUT
  }

  const items = Array.isArray((raw as { widgets?: unknown }).widgets)
    ? (raw as { widgets: unknown[] }).widgets
    : Array.isArray((raw as { items?: unknown }).items)
      ? (raw as { items: unknown[] }).items
      : []

  const seen = new Set<AdminWidgetId>()
  const sanitized = items.flatMap((item) => {
    if (!item || typeof item !== 'object') return []
    const candidate = item as { id?: unknown; width?: unknown }
    const mappedId = candidate.id === 'pipelineBottlenecks' ? 'draftDistribution' : candidate.id
    if (!isWidgetId(mappedId) || seen.has(mappedId)) return []
    seen.add(mappedId)
    return [{ id: mappedId, width: isWidgetWidth(candidate.width) ? candidate.width : ADMIN_WIDGET_DEFINITIONS[mappedId].defaultWidth }]
  })

  return sanitized.length > 0 ? sanitized : DEFAULT_ADMIN_WIDGET_LAYOUT
}

function readStoredLayout(): AdminWidgetItem[] {
  if (typeof window === 'undefined') {
    return DEFAULT_ADMIN_WIDGET_LAYOUT
  }

  try {
    const raw = window.localStorage.getItem(ADMIN_WIDGET_STORAGE_KEY)
    if (!raw) return DEFAULT_ADMIN_WIDGET_LAYOUT
    return sanitizeLayout(JSON.parse(raw))
  } catch {
    return DEFAULT_ADMIN_WIDGET_LAYOUT
  }
}

export function useAdminWidgetLayout() {
  const [items, setItems] = useState<AdminWidgetItem[]>(readStoredLayout)

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(ADMIN_WIDGET_STORAGE_KEY, JSON.stringify({ widgets: items }))
    } catch {
      // Ignore storage write failures; the layout still works in memory.
    }
  }, [items])

  useEffect(() => {
    function handleStorage(event: StorageEvent) {
      if (event.key !== ADMIN_WIDGET_STORAGE_KEY) return
      try {
        setItems(event.newValue ? sanitizeLayout(JSON.parse(event.newValue)) : DEFAULT_ADMIN_WIDGET_LAYOUT)
      } catch {
        setItems(DEFAULT_ADMIN_WIDGET_LAYOUT)
      }
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  const visibleIds = useMemo(() => new Set(items.map(item => item.id)), [items])

  function addWidget(id: AdminWidgetId) {
    setItems((prev) => {
      if (prev.some(item => item.id === id)) return prev
      return [...prev, makeDefaultItem(id)]
    })
  }

  function removeWidget(id: AdminWidgetId) {
    setItems((prev) => prev.filter(item => item.id !== id))
  }

  function setWidgetWidth(id: AdminWidgetId, width: AdminWidgetWidth) {
    setItems((prev) => prev.map(item => (item.id === id ? { ...item, width } : item)))
  }

  function resetLayout() {
    setItems(DEFAULT_ADMIN_WIDGET_LAYOUT)
  }

  return {
    items,
    setItems,
    visibleIds,
    addWidget,
    removeWidget,
    setWidgetWidth,
    resetLayout,
  }
}
